import React, { FC } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import styled from 'styled-components';

import {
  ArrowSize,
  LineColor,
  NodeSize,
  OPTION_NODE_TYPE,
} from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { ERROR_TEXT, NO_MATCH_TEXT } from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import generateAlphaNumericId from 'src/presentation/components/pages/ivr/helpers/generateAlphaNumericId';
import { IvrPathWithLink, Point } from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTypes';
import { TextNode } from './optionNode';
import { wrapLabel } from 'src/presentation/components/pages/shared/utils/textUtils';

export const IvrLink = ({
  highlighted,
  lineSize = 1,
  link,
  marginLeft,
  marginTop,
  selected,
}: {
  highlighted?: boolean;
  lineSize?: number;
  link: IvrPathWithLink;
  marginLeft: number;
  marginTop: number;
  selected?: boolean;
}) => {
  const id = generateAlphaNumericId();
  const pathProps = {
    d: link.svgLine,
    ...(link.showArrow && { markerEnd: `url(#arrow${id})` }),
    ...(link.showStartPoint && { markerStart: `url(#startMarker${id})` }),
  };

  const dataAid = `${link.source.id}-${link.target.id}${link.option ? `-${link.option}-${link.optionIndex}` : ''}`;

  return (
    <g transform={`translate(${marginLeft},${marginTop})`}>
      <LineGroup selected={selected || highlighted} strokeWidth={lineSize}>
        <path className="link" data-aid={dataAid} {...pathProps} markerUnits="userSpaceOnUse" />

        <React.Fragment>
          {link.showArrow && <ArrowMarker id={id} size={ArrowSize} />}
          {link.showStartPoint && <LineStartMarker id={id} />}
        </React.Fragment>
      </LineGroup>
      <DrawOption
        id={link.source.id}
        selected={selected}
        highlighted={highlighted}
        option={link.option}
        optionIndex={link.optionIndex}
        points={link.points}
      />
    </g>
  );
};

type DrawOptionType = {
  highlighted?: boolean;
  id: string;
  option?: string;
  optionIndex?: number;
  points: Point[];
  selected?: boolean;
};

const DrawOption: FC<DrawOptionType> = ({ highlighted, id, option, optionIndex, points, selected }) => {
  if (isEmpty(points) || !option) {
    return null;
  }

  const point1 = points[points.length - 1];
  const point0 = points[points.length - 2];

  const [x, y] = [
    point0[0] + (point1[0] - point0[0]) / 2 - NodeSize.OptionalNode.width / 2,
    point0[1] + (point1[1] - point0[1]) / 2 - NodeSize.OptionalNode.height / 2,
  ];

  const wrappedName = wrapLabel(option, 60, 'bold 10px Inter');

  const name = wrappedName.length > 1 ? `${wrappedName[0].trim()}...` : wrappedName[0].trim();

  const textId = `${id}-${!isNil(optionIndex) ? optionIndex : option.replace(' ', '-')}`;
  const data = {
    isError: option === ERROR_TEXT && !optionIndex,
    isNotMatch: option === NO_MATCH_TEXT && !optionIndex,
    optionIndex: optionIndex,
  };

  return (
    <TextNode
      y={0}
      x={0}
      width={NodeSize.OptionalNode.width}
      text={name}
      selected={selected}
      highlighted={highlighted}
      node={{ id: textId, parent: id, data, type: OPTION_NODE_TYPE }}
      id={textId}
      height={NodeSize.OptionalNode.height}
      className="pathOption"
      transform={`translate(${x}, ${y})`}
    />
  );
};

type ArrowMarkerProps = {
  id: string;
  size: number;
};

const ArrowMarker = ({ id, size }: ArrowMarkerProps) => {
  const viewBox = `0 ${-size} ${size * 2} ${size * 2}`;
  const dAttr = `M0,${-size + 1}L${size * 2 - 2},0L0,${size - 1}`;
  const marketProps = {
    id: `arrow${id}`,
    viewBox,
    refX: size * 2 - 2,
    refY: 0,
    markerWidth: size,
    markerHeight: size,
    orient: 'auto-start-reverse',
  };
  return (
    <marker {...marketProps}>
      <path d={dAttr} fill="none" stroke="context-stroke" strokeWidth="2" />
    </marker>
  );
};

const LineStartMarker = ({ id }) => {
  return (
    <marker id={`startMarker${id}`} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="12" markerHeight="12">
      <circle cx="5" cy="5" r="3" fill="white" stroke="context-stroke" strokeWidth="0.8" />
    </marker>
  );
};

const LineGroup = styled.g`
  pointer-events: auto;
  fill: none;

  stroke: ${p => (p.selected ? p.theme.colors.purple200 : LineColor)};
`;
